<script setup lang="ts">
import IconButton from '@/components/Buttons/IconButton.vue'
import { StackLayout } from '@/components/Layouts'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import BaseDialog from '@/components/Modals/Dialog/BaseDialog.vue'
import { EColors } from '@/types/constants/ColorValues'
import type { DeviceModel } from '@/types/models/DeviceModel'
import type { PermissionsEnums } from '@/types/permissions/PermissionsEnum'
import type { AlertSocketMessageModel } from '@/types/socket/SocketAlertModel'
import type { SocketDeviceDetectionModel } from '@/types/socket/SocketDetectionModel'
import { addToQuery } from '@/utils/queryUtils'
import { isMobile, isMobileEvent } from '@/utils/viewsUtils'
import DeviceDetailView from '@/views/DevicesView/DeviceDetailView.vue'
import { Pane, Splitpanes } from 'splitpanes'
import { nextTick, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import DeviceTableCardList from './DeviceTableCardList.vue'

type Props = {
    data: DeviceModel[]
    statuses: AlertSocketMessageModel[]
    detections?: SocketDeviceDetectionModel[]
    loading?: boolean
    showNextEvent?: boolean
    showNextCheckpoint?: boolean
    editPermission?: PermissionsEnums
    editButtonHref?: string
    disableClick?: boolean
}
defineProps<Props>()
type Emits = {
    (e: 'refresh', device?: Partial<DeviceModel>): void
}
defineEmits<Emits>()

const route = useRoute()
const selectedDevice = ref<Partial<DeviceModel>>()

watch(
    () => route.query.preview,
    newPreviewId =>
        newPreviewId
            ? (selectedDevice.value = {
                  ...selectedDevice.value,
                  device_id: parseInt(newPreviewId.toString())
              })
            : (selectedDevice.value = undefined),
    { immediate: true }
)

const getDeviceStatus = (device: Partial<DeviceModel>) => ({
    battery: device.battery,
    plugged: device.plugged,
    signal: device.signal,
    network_type: device.network_type
})

const handleSelectDevice = async (device?: DeviceModel) => {
    await nextTick(() => addToQuery({ preview: device?.device_id ?? device?.id }, true))
    selectedDevice.value = device
}

const isMobileLayout = ref(isMobile())
isMobileEvent(e => (isMobileLayout.value = e.matches))

provide('isResponsiveTablet', ref(true))
provide('isResponsiveDesktop', ref(true))
provide('noAddToQuery', ref(true))
</script>

<template>
    <splitpanes vertical>
        <pane min-size="15">
            <StackLayout
                direction="column"
                :gap="8"
                :class="`left-side ${selectedDevice ? 'outlook-mode' : ''}`">
                <div :class="`${selectedDevice ? 'outlook-layout' : ''} full-width`">
                    <DeviceTableCardList
                        :data="data"
                        :selectedDevice="selectedDevice"
                        :statuses="statuses"
                        :detections="detections"
                        :disableClick="disableClick"
                        :showNextEvent="showNextEvent"
                        :showNextCheckpoint="showNextCheckpoint"
                        :editPermission="editPermission"
                        :editButtonHref="editButtonHref"
                        :loading="loading"
                        class="grid-outlook">
                        <template #iconHeader="device">
                            <slot name="iconHeader" v-bind="device" />
                        </template>
                        <template #cardContent="device">
                            <slot name="cardContent" v-bind="device" />
                        </template>
                    </DeviceTableCardList>
                </div>
                <slot />
            </StackLayout>
        </pane>
        <!-- <DividerItem v-if="selectedDevice" orientation="vertical" variant="middle" isResponsive /> -->
        <pane min-size="25" v-if="selectedDevice && !isMobileLayout">
            <StackLayout class="right-side full-width">
                <StackLayout
                    v-if="selectedDevice"
                    direction="column"
                    :gap="8"
                    class="full-width fit-height">
                    <IconButton
                        icon="arrow-right"
                        :type="EColors.PRIMARY"
                        @click="handleSelectDevice(undefined)"
                        class="fit-content" />
                    <slot
                        name="outlookMode"
                        v-bind="{
                            device: {
                                ...selectedDevice,
                                ...getDeviceStatus(selectedDevice),
                                ...(statuses?.find(
                                    item =>
                                        item.device_id ===
                                        (selectedDevice?.device_id ?? selectedDevice?.id)
                                ) ?? {})
                            }
                        }">
                        <DeviceDetailView
                            :device="selectedDevice"
                            @refresh="$emit('refresh', $event)"
                            isOutlookMode
                            class="full-width" />
                    </slot>
                </StackLayout>
            </StackLayout>
        </pane>
    </splitpanes>
    <BaseDialog
        :open="!!selectedDevice && isMobileLayout"
        @close="selectedDevice = undefined"
        full-screen
        class="outlook-mode-modal"
        noFooter>
        <template #content>
            <StackLayout direction="column" :gap="8" class="full-height">
                <IconButton
                    icon="arrow-right"
                    :type="EColors.PRIMARY"
                    @click="selectedDevice = undefined"
                    class="fit-content" />
                <slot name="outlookMode" v-bind="{ device: selectedDevice }" />
            </StackLayout>
        </template>
    </BaseDialog>

    <StackLayout v-if="!data?.length" class="align-self-center no-record">
        <LoadingSpinner :isLoading="!!loading" />
        {{ $t('common.noRecord') }}
    </StackLayout>
</template>

<style scoped lang="scss">
.device-table {
    overflow-y: auto;
}
.left-side {
    height: 100%;
    width: 100%;
    overflow-y: auto;
    padding: 0 4px 8px 8px;
}
.right-side {
    height: 100%;
    overflow-y: auto;
    padding: 0 8px 8px 0;
}
.grid-outlook {
    overflow-y: auto;
}

.outlook-mode {
    direction: rtl;
}

.outlook-layout {
    direction: ltr;
    container: outlook-layout / inline-size;
}

@container outlook-layout (width < 400px) {
    .grid-class {
        grid-template-columns: repeat(1, minmax(0, 1fr));
    }
}
@container outlook-layout (width > 400px) {
    .grid-class {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
@container outlook-layout (width > 600px) {
    .grid-class {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

.outlook-mode-modal {
    & :deep(.device-detail) {
        height: 100%;
        overflow-y: auto;
    }
    & :deep(.b-tabs) {
        height: 100%;
    }
    & :deep(.tab-content) {
        height: 100%;
        overflow-y: auto;
        padding-right: 8px;
    }

    & :deep(.full-table-layout) {
        padding: unset;
    }
}

.split {
    display: flex;
    flex-direction: row;
}

.gutter {
    background-color: #eee;
    background-repeat: no-repeat;
    background-position: 50%;
}

.gutter.gutter-horizontal {
    background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAeCAYAAADkftS9AAAAIklEQVQoU2M4c+bMfxAGAgYYmwGrIIiDjrELjpo5aiZeMwF+yNnOs5KSvgAAAABJRU5ErkJggg==');
    cursor: col-resize;
}
</style>
